import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Grid,
    Select,
    MenuItem,
    SelectChangeEvent,
    Typography,
    useTheme,
    CircularProgress,
} from '@mui/material';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer
} from 'recharts';
import Daterange from '../FinancialReport/Daterange';
import { Search } from '@mui/icons-material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';

interface HashData {
    expenseDate: number;
    totalHashrate: number;
    totalExpense: number;
    expensePerHashrate: number;
    time?: string;
}

const CustomTooltip = ({
    active,
    payload,
    label,
}: {
    active?: boolean;
    payload?: any;
    label?: string;
}) => {
    if (active && payload && payload.length > 0) {
        const data = payload[0].payload;

        return (
            <Box
                sx={{
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    p: 1.5,
                    boxShadow: 3,
                }}
            >
                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    {data.time}
                </Typography>
                <Typography variant="body2">
                    <strong>Expense/Hashrate:</strong>{' '}
                    {Number(data.expensePerHashrate).toFixed(8)}{' '}
                    <Box component="span" sx={{ color: '#7a7e85', fontSize: '0.75rem' }}>
                        BTC/PH/day
                    </Box>
                </Typography>
                <Typography sx={{ fontSize: '0.70rem', color: '#7a7e85' }}>
                    <strong>Hashrate:</strong> {data.totalHashrate} PH/day
                </Typography>
                <Typography sx={{ fontSize: '0.70rem', color: '#7a7e85' }}>
                    <strong>Expense:</strong> {data.totalExpense.toFixed(6)} BTC
                </Typography>

            </Box>
        );
    }

    return null;
};



function HashBoard() {
    const theme = useTheme();

    const today = new Date();
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(today.getDate() - 10);

    const [fromDate, setFromDate] = useState<Date | null>(tenDaysAgo);
    const [toDate, setToDate] = useState<Date | null>(today);
    const [searchTrigger, setSearchTrigger] = useState(false);
    const [selectedType, setSelectedType] = useState<string[]>(['SEGPOOL']);
    const [selectedCoin, setSelectedCoin] = useState<string>('btc');
    const [hashData, setHashData] = useState<HashData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const toggleType = (type: string) => {
        setSelectedType(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };

    const handleCoinChange = (event: SelectChangeEvent<string>) => {
        setSelectedCoin(event.target.value);
    };

    const isSelected = (type: string) => selectedType.includes(type);

    useEffect(() => {
        const fetchData = async () => {
            if (!fromDate || !toDate || selectedType.length === 0) return;

            setIsLoading(true);

            const engineParam = selectedType.length > 1 ? 'ALL' : selectedType[0];
            const startDate = fromDate.toISOString().split('T')[0];
            const endDate = toDate.toISOString().split('T')[0];

            try {
                const response = await axios.get(`${API_PATH}/dashboard`, {
                    ...getAxiosConfig(),
                    params: {
                        engine: engineParam,
                        coin: selectedCoin,
                        startDate,
                        endDate,
                    },
                });

                const formatted = response.data
                    .map((item: HashData) => ({
                        ...item,
                        time: new Date(item.expenseDate).toLocaleDateString('en-GB'),
                    }))
                    .sort((a: HashData, b: HashData) => new Date(a.expenseDate).getTime() - new Date(b.expenseDate).getTime());


                setHashData(formatted);
            } catch (error) {
                console.error('Failed to fetch hash data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [searchTrigger, fromDate, toDate, selectedType, selectedCoin]);

    return (
        <Box sx={{ m: 2 }}>
            <Box
                display="flex"
                alignItems="center"
                // position="sticky"
                top="70px"
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
                gap={1}
                zIndex={100}
                sx={{
                    mb: 2,
                    backgroundColor: '#d1e8ff',
                    padding: { xs: 1, sm: 1.5 },
                    borderRadius: 1,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontFamily: 'monospace',
                        ml: { xs: 0, sm: '1rem' },
                        color: '#1565c0',
                        textAlign: { xs: 'center', sm: 'left' },
                        width: '100%',
                    }}
                >
                    EXPENSE PER HASH
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
                <Daterange
                    fromDate={fromDate}
                    toDate={toDate}
                    setFromDate={setFromDate}
                    setToDate={setToDate}
                    setSearchTrigger={setSearchTrigger}
                    searchTrigger={searchTrigger}
                />

                <Button
                    variant={isSelected('SEGPOOL') ? 'contained' : 'outlined'}
                    onClick={() => toggleType('SEGPOOL')}
                >
                    SEGPOOL
                </Button>
                <Button
                    variant={isSelected('CLOUD') ? 'contained' : 'outlined'}
                    onClick={() => toggleType('CLOUD')}
                >
                    CLOUD
                </Button>

                <Select
                    size="small"
                    value={selectedCoin}
                    onChange={handleCoinChange}
                    displayEmpty
                    sx={{ minWidth: 100 }}
                >
                    <MenuItem value="btc">BTC</MenuItem>
                    <MenuItem value="kaspa">KAS</MenuItem>
                    <MenuItem value="alph">ALPH</MenuItem>
                    <MenuItem value="ltc">LTC</MenuItem>
                </Select>

                <Grid item>
                    <Button
                        variant="contained"
                        size="small"
                        sx={{ py: 1 }}
                        onClick={() => setSearchTrigger(prev => !prev)}
                    >
                        <Search />
                    </Button>
                </Grid>
            </Box>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <CircularProgress color="primary" />
                </Box>
            ) : hashData.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                        No data available
                    </Typography>
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height={320}>
                    <AreaChart data={hashData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorHash" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8} />
                                <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <XAxis dataKey="time" stroke="#8884d8" />
                                <YAxis
                                    stroke="#8884d8"
                                    tickCount={8}
                                    domain={[
                                        (dataMin: number) => dataMin - dataMin * 0.2,
                                        'auto',
                                    ]}
                                />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                            dataKey="expensePerHashrate"
                            stroke={theme.palette.primary.main}
                            fillOpacity={1}
                            fill="url(#colorHash)"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </Box>
    );
}

export default HashBoard;
